import React from "react";
import { toast } from "react-toastify";
import { Paper } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { PersonAddAltOutlined } from "@mui/icons-material";

import { AppConfirmDialog } from "components/Shared/AppConfirmDialog";
import InformationTile from "components/Shared/InformationTile";

import { useGetDDLAuthorizationListQuery } from "store/services/authorizationApi";
import { useAddUserMutation } from "store/services/userApi";
import { useUpdateVendorDataMutation } from "store/services/CanUsers/vendorApi";
import { useUpdateCustomerUserDataMutation } from "store/services/customerUserApi";

import { useAuth } from "hooks";


const UserAccess = ({ sourceDetail, typeRefSource }) => {
  if (!typeRefSource) {
    typeRefSource =
      sourceDetail?.type === "Customer" ? "Customer" : "VendorProfile";
  }
  const [showDialog, setShowDialog] = React.useState(false);

  // RTK Query for authorizations
  const { data: authorizations, isLoading: loadingAuthorizations } =
    useGetDDLAuthorizationListQuery({ levels: ["Entity"], types: ["User"] });

  const _entityUserAuthorization = authorizations?.map((el) => el?._id) ?? [];

  const { loggedInUser } = useAuth();

  const userPayload = {
    entity:
      
      loggedInUser?.viewEntity?.entity?._id,
    authorizations: _entityUserAuthorization,
    defaultAuthorization: _entityUserAuthorization[0],
    name: sourceDetail?.name,
    email: sourceDetail?.email,
    typeRef: sourceDetail?._id,
    typeRefSource,
    autoGeneratePassword: true,
  };

  // RTK Query mutations
  const [createUser, { isLoading: creatingUser }] = useAddUserMutation();

  const [updateVendor, { isLoading: updatingVendor }] =
    useUpdateVendorDataMutation();
  const [updateCustomer, { isLoading: updatingCustomer }] =
    useUpdateCustomerUserDataMutation();

  const isUpdating = updatingVendor || updatingCustomer;

  // Method to handle user access creation
  const handleCreateUserAccess = async () => {
    if (Object.values(userPayload).some((val) => !val))
      return toast.error("Insufficient User data!");

    try {
      const user = await createUser(userPayload).unwrap();
      if (!user) return;

      if (typeRefSource === "VendorProfile") {
        await updateVendor({
          _id: sourceDetail?._id,
          userRef: user?._id,
          
        }).unwrap();
      } else if (typeRefSource === "Customer") {
        await updateCustomer({
          _id: sourceDetail?._id,
          userRef: user?._id,
        }).unwrap();
      }

      toast.success("User Access successfully created!");
    } catch (err) {
      toast.error(
        err?.data?.message ?? err?.message ?? "Couldn't create user!"
      );
    } finally {
      setShowDialog(false);
    }
  };

  if (sourceDetail?.userRef) return null;

  return (
    <>
      <LoadingButton
        startIcon={<PersonAddAltOutlined />}
        color="success"
        variant="outlined"
        loading={creatingUser || isUpdating}
        loadingPosition="start"
        onClick={() => setShowDialog(true)}
        disabled={loadingAuthorizations}
      >
        User Access
      </LoadingButton>

      <AppConfirmDialog
        title="Create User Access?"
        open={showDialog}
        onConfirm={handleCreateUserAccess}
        onClose={() => setShowDialog(false)}
        loading={creatingUser || isUpdating}
      >
        <div className="mb-3">
          Are you sure you want to create user access for{" "}
          <span className="font-medium text-blue-600 dark:text-blue-200">
            {sourceDetail?.fullName}
          </span>
          ?
        </div>

        <Paper variant="outlined" className="grid grid-cols-1 gap-3 p-4">
          <InformationTile title="Name" subtitle={userPayload?.name} />
          <InformationTile title="Email" subtitle={userPayload?.email} />
          <InformationTile title="Authorization" subtitle="Entity User" />
        </Paper>
      </AppConfirmDialog>
    </>
  );
};

export default UserAccess;
